import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Send, RefreshCw, Sparkles, Mail, X, AlertTriangle } from 'lucide-react';
import { getGeminiResponse } from './lib/gemini';
import { supabase, type LearningSession, updateSessionFeedback } from './lib/supabase';
import { LoadingSpinner } from './components/LoadingSpinner';
import { ConversationList } from './components/ConversationList';
import type { ConversationEntry } from './types';
import { UpdatePrompt } from './components/UpdatePrompt';
import { InstallPrompt } from './components/InstallPrompt';
import { OfflineIndicator } from './components/OfflineIndicator';
import { useServiceWorker } from './hooks/useServiceWorker';
import { useAnalytics } from './hooks/useAnalytics';
import { usePerformanceMonitoring } from './hooks/usePerformanceMonitoring';
import { trackError, setUserContext, clearUserContext } from './lib/monitoring';
import { GridBackground } from './components/GridBackground';
import { SynthflowBackground } from './components/SynthflowBackground';
import { FloatingElements } from './components/FloatingElements';
import { MouseLight } from './components/MouseLight';
import AuthModal from './components/AuthModal';
import FeedbackModal from './components/FeedbackModal';
import ProfileButton from './components/ProfileButton';
import ConversationSidebar from './components/ConversationSidebar';
import AnimatedLogo from './components/AnimatedLogo';
import { Footer } from './components/Footer';

function App() {
  const { isUpdateAvailable, updateServiceWorker } = useServiceWorker();
  const [question, setQuestion] = useState('');
  const [conversation, setConversation] = useState<ConversationEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState<LearningSession[]>([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [feedbackModal, setFeedbackModal] = useState<{
    isOpen: boolean;
    sessionId?: string;
    initialRating: number;
  }>({
    isOpen: false,
    initialRating: 5
  });
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState<string>();
  const analytics = useAnalytics(user?.id);
  const { startMeasurement, endMeasurement } = usePerformanceMonitoring();

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const newUser = session?.user ?? null;
      setUser(newUser);
      
      if (newUser) {
        setUserContext({
          id: newUser.id,
          email: newUser.email!,
        });
      } else {
        clearUserContext();
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const newUser = session?.user ?? null;
      setUser(newUser);
      
      if (newUser) {
        setUserContext({
          id: newUser.id,
          email: newUser.email!,
        });
      } else {
        clearUserContext();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      loadSessions();
    }
  }, [user]);

  const loadSessions = async () => {
    try {
      const { data, error } = await supabase
        .from('learning_sessions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSessions(data || []);
    } catch (err) {
      console.error('Error loading sessions:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const startTime = performance.now();
    startMeasurement('ai_response');

    try {
      const history = conversation.map(entry => 
        `User: ${entry.question}\nRumi: ${entry.response}`
      );

      const response = await getGeminiResponse(question, history, user?.id);
      
      const newEntry = {
        question,
        response: response.text,
        media: response.media
      };

      setConversation(prev => [...prev, newEntry]);

      // Save to database if user is logged in
      if (user) {
        const { data, error } = await supabase
          .from('learning_sessions')
          .insert({
            user_id: user.id,
            subject: question.slice(0, 50) + (question.length > 50 ? '...' : ''),
            question,
            response: response.text,
            created_at: new Date().toISOString()
          })
          .select()
          .single();

        if (error) throw error;
        
        if (data) {
          setActiveSessionId(data.id);
          await loadSessions();
        }
      }

      const duration = performance.now() - startTime;
      analytics.trackConversation(question.length, duration);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get response';
      setError(errorMessage);
      console.error('Error:', err);
      trackError(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setIsLoading(false);
      setQuestion('');
      endMeasurement('ai_response');
    }
  };

  const handleReset = () => {
    setConversation([]);
    setQuestion('');
    setError(null);
    setActiveSessionId(undefined);
  };

  const handleSessionSelect = async (session: LearningSession) => {
    try {
      setActiveSessionId(session.id);
      setConversation([{
        question: session.question,
        response: session.response
      }]);
      setIsSidebarOpen(false);
    } catch (err) {
      console.error('Error loading session:', err);
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    try {
      const { error } = await supabase
        .from('learning_sessions')
        .delete()
        .eq('id', sessionId);

      if (error) throw error;

      if (sessionId === activeSessionId) {
        handleReset();
      }

      setSessions(prev => prev.filter(session => session.id !== sessionId));
    } catch (err) {
      console.error('Error deleting session:', err);
    }
  };

  const handleSessionUpdate = (updatedSession: LearningSession) => {
    setSessions(prev => prev.map(session => 
      session.id === updatedSession.id ? updatedSession : session
    ));
  };

  const handleFeedback = async (sessionId: string, rating: number) => {
    setFeedbackModal({
      isOpen: true,
      sessionId,
      initialRating: rating
    });
  };

  const handleFeedbackSubmit = async (rating: number, comment: string) => {
    try {
      if (!feedbackModal.sessionId) return;

      await updateSessionFeedback(feedbackModal.sessionId, rating, comment);
      
      // Update local state
      setSessions(prev => prev.map(session => 
        session.id === feedbackModal.sessionId
          ? { ...session, feedback: rating, feedback_comment: comment }
          : session
      ));

      setConversation(prev =>
        prev.map(entry =>
          entry.id === feedbackModal.sessionId
            ? { ...entry, feedback: rating, feedbackComment: comment }
            : entry
        )
      );

      // Close the modal
      setFeedbackModal({ isOpen: false, initialRating: 5 });
    } catch (err) {
      console.error('Error submitting feedback:', err);
    }
  };

  const handleLogin = () => {
    setAuthModalOpen(true);
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-950">
      <GridBackground />
      <SynthflowBackground />
      <FloatingElements />
      <MouseLight mousePosition={mousePosition} />
      
      <ConversationSidebar
        isOpen={isSidebarOpen}
        onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        sessions={sessions}
        activeSessionId={activeSessionId}
        onSessionSelect={handleSessionSelect}
        onNewSession={handleReset}
        onDeleteSession={handleDeleteSession}
        onSessionUpdate={handleSessionUpdate}
      />
      
      <div className={`relative z-10 transition-all duration-300 ${isSidebarOpen ? 'lg:ml-80' : ''}`}>
        <div className="min-h-screen flex flex-col">
          <div className="flex-1 p-4 sm:p-6 md:p-8">
            <div className="max-w-4xl mx-auto">
              <div className="mb-16">
                <div className="flex items-center justify-between">
                  <div className="flex items-center -ml-8 -mt-6">
                    <div className="relative w-40 h-24 -mr-4">
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] h-[280px] scale-[0.25]">
                        <AnimatedLogo />
                      </div>
                    </div>
                    <h1 className="text-4xl font-display font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent drop-shadow-[0_0_15px_rgba(59,130,246,0.3)]">
                      Ask Rumi
                    </h1>
                  </div>
                  <div className="flex items-center space-x-4">
                    {user ? (
                      <div className="flex items-center space-x-4">
                        <ProfileButton email={user.email!} />
                        <button
                          onClick={() => supabase.auth.signOut()}
                          className="btn-secondary px-4 py-2 rounded-lg text-sm"
                        >
                          Sign Out
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={handleLogin}
                        className="btn-primary text-sm flex items-center space-x-2"
                      >
                        <Sparkles className="w-4 h-4" />
                        <span>Sign In</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-8">
                <ConversationList
                  conversation={conversation}
                  onFeedback={handleFeedback}
                  user={user}
                />

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="relative">
                    <label
                      htmlFor="question"
                      className="block text-sm font-medium text-slate-300 mb-2"
                    >
                      {conversation.length > 0 ? 'Ask a follow-up question...' : 'Ask your question here...'}
                    </label>
                    <textarea
                      id="question"
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      className="w-full input-field rounded-xl min-h-[120px] resize-none"
                      placeholder={conversation.length > 0 
                        ? "Ask a follow-up question based on the previous conversation..."
                        : "Try asking a math problem for step-by-step help..."
                      }
                      disabled={isLoading}
                    />
                  </div>

                  <div className="flex justify-center">
                    <button
                      type="submit"
                      className="btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed px-8"
                      disabled={!question.trim() || isLoading}
                    >
                      {isLoading ? (
                        <>
                          <LoadingSpinner size="sm" />
                          <span>Getting Response...</span>
                        </>
                      ) : (
                        <>
                          <span>Ask Rumi</span>
                          <Send className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </form>

                {error && (
                  <div className="glass-panel bg-red-500/10 border-red-500/20 p-4 rounded-xl flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-red-400">Error</h3>
                      <p className="mt-1 text-sm text-red-300">{error}</p>
                      {error.includes('API key') && (
                        <p className="mt-2 text-sm text-red-300">
                          Please make sure your environment variables are properly configured.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {conversation.length === 0 && !isLoading && (
                  <div className="glass-panel-glow rounded-xl p-6">
                    <div className="text-slate-400">
                      <p>Try asking:</p>
                      <ul className="list-disc list-inside mt-2 space-y-2">
                        <li>"Solve the equation: 2x + 5 = 13"</li>
                        <li>"Explain how photosynthesis works"</li>
                        <li>"What are the key events of World War II?"</li>
                        <li>"I need help with calculus, can you tutor me through derivatives and integrals? Please start with the basics and provide practice problems."</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Footer />
        </div>
      </div>

      {authModalOpen && (
        <AuthModal
          isOpen={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
        />
      )}
      
      {feedbackModal.isOpen && (
        <FeedbackModal
          isOpen={feedbackModal.isOpen}
          onClose={() => setFeedbackModal({ isOpen: false, initialRating: 5 })}
          onSubmit={handleFeedbackSubmit}
          initialRating={feedbackModal.initialRating}
        />
      )}
      
      <OfflineIndicator />
      <InstallPrompt />
      {isUpdateAvailable && <UpdatePrompt onUpdate={updateServiceWorker} />}
    </div>
  );
}

export default App;